import React from 'react'
import { CreditApplication, CreditApplicationStatus } from '_gql'

import MoreInfoNeeded from '@sputnik/pages/Dashboard/Status/MoreInfoNeeded'
import { EDITABLE_STATUSES, MAJOR_ACCOUNT_CHANGE, MIGRATION } from '@sputnik/resources/constants'

import ApprovedStatusTile from './ApprovedStatusTile'
import AwaitingSignatureStatusTile from './AwaitingSignatureStatusTile'
import CompanyBuiltStatusTile from './CompanyBuiltStatusTile'
import CorrectionsRequestedStatusTile from './CorrectionsRequestedStatusTile'
import InReviewSubmittedStatusTile from './InReviewSubmittedStatusTile'
import MajorAccountChangeStatusTile from './MajorAccountChangeStatusTile'
import MigrationCompleteStatusTile from './MigrationCompleteStatusTile'
import MigrationInProgressStatusTile from './MigrationInProgressStatusTile'
import MigrationPendingStatusTile from './MigrationPendingStatusTile'
import NotStartedOrInProgressStatusTile from './NotStartedOrInProgressStatusTile'
import OfferAcceptedStatusTile from './OfferAcceptedStatusTile'
import RefreshApplicationStatusTile from './RefreshApplicationStatusTile'
import RejectedStatusTile from './RejectedStatusTile'
import ReviewOfferStatusTile from './ReviewOfferStatusTile'

interface DashboardApplicationProps {
  creditApp: CreditApplication
  refetch: () => void
}

const DashboardApplication = ({ creditApp, refetch }: DashboardApplicationProps) => {
  const { status, recordType, savedSectionsInfo } = creditApp || {}
  const isMigration = recordType === MIGRATION
  const isMAC = recordType === MAJOR_ACCOUNT_CHANGE

  if (!status || !Object.keys(CreditApplicationStatus).includes(status as string)) return null

  if (isMAC && EDITABLE_STATUSES.includes(status)) {
    return <MajorAccountChangeStatusTile creditApp={creditApp} />
  }

  if (isMigration) {
    if (status === CreditApplicationStatus.NOT_STARTED || status === CreditApplicationStatus.IN_PROGRESS) {
      return <MigrationInProgressStatusTile creditApp={creditApp} />
    }

    if (
      status === CreditApplicationStatus.IN_REVIEW ||
      status === CreditApplicationStatus.SUBMITTED ||
      status === CreditApplicationStatus.APPROVED
    ) {
      return <MigrationPendingStatusTile creditApp={creditApp} />
    }

    if (status === CreditApplicationStatus.MIGRATION_COMPLETE) {
      return <MigrationCompleteStatusTile creditApp={creditApp} />
    }
  }

  if (
    status === CreditApplicationStatus.NOT_STARTED ||
    (status === CreditApplicationStatus.IN_PROGRESS &&
      (!savedSectionsInfo?.length || savedSectionsInfo?.every((section) => !section?.completedAt)))
  ) {
    return <NotStartedOrInProgressStatusTile creditApp={creditApp} />
  }

  if (status === CreditApplicationStatus.IN_PROGRESS || status === CreditApplicationStatus.AWAITING_SIGNATURE) {
    // returns the % complete tile and the amend application tile depending on if the user is the authorized signer
    return (
      <AwaitingSignatureStatusTile
        creditApp={creditApp}
        refetch={refetch}
      />
    )
  }

  if (status === CreditApplicationStatus.IN_REVIEW || status === CreditApplicationStatus.SUBMITTED) {
    // this component is also being rendered inside MoreInfoNeededStatusTile when they have submitted all documents
    return <InReviewSubmittedStatusTile creditApp={creditApp} />
  }

  if (status === CreditApplicationStatus.OFFER_ACCEPTED) {
    return <OfferAcceptedStatusTile creditApp={creditApp} />
  }

  if (status === CreditApplicationStatus.APPROVED || status === CreditApplicationStatus.BUILD_ACCOUNT) {
    return <ApprovedStatusTile creditApp={creditApp} />
  }

  if (status === CreditApplicationStatus.COMPANY_BUILT) {
    return <CompanyBuiltStatusTile creditApp={creditApp} />
  }

  if (status === CreditApplicationStatus.REJECTED) {
    return <RejectedStatusTile creditApp={creditApp} />
  }

  if (status === CreditApplicationStatus.REVIEW_OFFER) {
    return <ReviewOfferStatusTile creditApp={creditApp} />
  }

  if (status === CreditApplicationStatus.MORE_INFO_NEEDED) {
    return (
      <MoreInfoNeeded
        creditApp={creditApp}
        refetch={refetch}
      />
    )
  }

  if (
    status === CreditApplicationStatus.CLOSED_LOST ||
    status === CreditApplicationStatus.EXPIRED ||
    status === CreditApplicationStatus.CLOSED_AS_DUPLICATE ||
    status === CreditApplicationStatus.CUSTOMER_CANCELED
  ) {
    return <RefreshApplicationStatusTile creditApp={creditApp} />
  }

  if (status === CreditApplicationStatus.CORRECTION_REQUESTED) {
    return <CorrectionsRequestedStatusTile creditApp={creditApp} />
  }

  return null
}

export default DashboardApplication
